import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import he from './locales/he.json';
import ar from './locales/ar.json';
import en from './locales/en.json';
import ru from './locales/ru.json';

// Detect language from subdomain or query parameter
const detectLanguage = () => {
  // Check query parameter first (?lang=he/ar/en/ru)
  // Used for: dev (afnu-dev:8080) and manual override
  const urlParams = new URLSearchParams(window.location.search);
  const langParam = urlParams.get('lang');
  if (langParam === 'he' || langParam === 'ar' || langParam === 'en' || langParam === 'ru') {
    return langParam;
  }

  // Check subdomain - works for both production and staging
  // Production: www.afnu.co.il -> Hebrew, ar/en/ru.afnu.co.il -> Arabic/English/Russian
  // Staging: staging.afnu.co.il -> Hebrew, ar/en/ru.staging.afnu.co.il -> Arabic/English/Russian
  const hostname = window.location.hostname;
  if (hostname.startsWith('ar.')) {
    return 'ar';
  }
  if (hostname.startsWith('en.')) {
    return 'en';
  }
  if (hostname.startsWith('ru.')) {
    return 'ru';
  }

  // Default to Hebrew
  return 'he';
};

const currentLanguage = detectLanguage();

// Language to direction mapping
const directionMap = {
  he: 'rtl',
  ar: 'rtl',
  en: 'ltr',
  ru: 'ltr'
};

i18n
  .use(initReactI18next)
  .init({
    resources: {
      he: {
        translation: he
      },
      ar: {
        translation: ar
      },
      en: {
        translation: en
      },
      ru: {
        translation: ru
      }
    },
    lng: currentLanguage,
    fallbackLng: 'he',
    interpolation: {
      escapeValue: false // React already does escaping
    },
    react: {
      useSuspense: false // Disable suspense mode for compatibility with React 17
    },
    saveMissing: true,
    missingKeyHandler: (lng, _ns, key) => {
      console.warn(`Missing translation key: ${key} for language: ${lng}`);
    }
  });

// Meta tag configurations for each language
const metaConfig = {
  he: {
    title: 'עפנו - חופשות סודיות',
    description: 'מהיום כבר לא צריך להכנס למינוס כדי לצאת לחופשה בחול. התקדמו עוד היום לחופשת הלואו-קוסט של עפנו והזמינו חופשה הכוללת טיסה+מלון במחיר ללא תחרות!',
    keywords: 'חופשות זולות, טיסות זולות, מלונות זולות, חבילות נופש, טיסה ומלון, עפנו, לואו קוסט, חופשות סודיות',
    ogTitle: 'עפנו - חופשות סודיות',
    ogDescription: 'מהיום כבר לא צריך להכנס למינוס כדי לצאת לחופשה בחול. התקדמו עוד היום לחופשת הלואו-קוסט של עפנו',
    metaTitle: 'עפנו - עם מחירים כאלו חבל להישאר בבית',
    canonical: 'https://www.afnu.co.il/',
    jsonLd: {
      name: 'עפנו - חופשות סודיות',
      description: 'מזמינים חבילות נופש לואו-קוסט רק בעפנו - כי מהיום כבר לא צריך להסתבך כדי להזמין טיסות ומלונות לחו"ל במחירים זולים',
      slogan: 'חופשות סודיות (טיסה ומלון) שעולם התיירות הסתיר מכם עד היום'
    }
  },
  ar: {
    title: 'AFNU - عطلات سرية',
    description: 'من اليوم لم تعد بحاجة للدخول في الديون للذهاب في عطلة في الخارج. تقدموا اليوم لعطلة AFNU منخفضة التكلفة واحجزوا عطلة تشمل رحلة طيران+فندق بسعر لا منافس له!',
    keywords: 'عطلات رخيصة, رحلات طيران رخيصة, فنادق رخيصة, باقات سياحية, رحلة وفندق, AFNU, تكلفة منخفضة, عطلات سرية',
    ogTitle: 'AFNU - عطلات سرية',
    ogDescription: 'من اليوم لم تعد بحاجة للدخول في الديون للذهاب في عطلة في الخارج. تقدموا اليوم لعطلة AFNU منخفضة التكلفة',
    metaTitle: 'AFNU - مع أسعار كهذه من الخسارة البقاء في المنزل',
    canonical: 'https://ar.afnu.co.il/',
    jsonLd: {
      name: 'AFNU - عطلات سرية',
      description: 'احجز باقات سياحية منخفضة التكلفة فقط في AFNU - لأنه من اليوم لم تعد بحاجة للتعقيد لحجز رحلات وفنادق للخارج بأسعار رخيصة',
      slogan: 'عطلات سرية (رحلة وفندق) أخفاها عالم السياحة عنكم حتى اليوم'
    }
  },
  en: {
    title: 'AFNU - Secret Holidays',
    description: 'You no longer need to go into debt to take a holiday abroad. Get your low-cost AFNU holiday today and book a vacation that includes flight+hotel at an unbeatable price!',
    keywords: 'cheap holidays, cheap flights, cheap hotels, vacation packages, flight and hotel, AFNU, low cost, secret holidays',
    ogTitle: 'AFNU - Secret Holidays',
    ogDescription: 'You no longer need to go into debt to take a holiday abroad. Get your low-cost AFNU holiday today',
    metaTitle: 'AFNU - With prices like these, it\'s a shame to stay home',
    canonical: 'https://en.afnu.co.il/',
    jsonLd: {
      name: 'AFNU - Secret Holidays',
      description: 'Book low-cost vacation packages only at AFNU - because you no longer need to struggle to book cheap flights and hotels abroad',
      slogan: 'Secret holidays (flight and hotel) that the tourism industry has hidden from you until today'
    }
  },
  ru: {
    title: 'AFNU - Секретные каникулы',
    description: 'Теперь вам больше не нужно влезать в долги, чтобы поехать на отдых за границу. Получите свой недорогой отпуск AFNU сегодня и забронируйте отдых, включающий авиабилет+отель по непревзойденной цене!',
    keywords: 'дешевые каникулы, дешевые авиабилеты, дешевые отели, туристические пакеты, авиабилет и отель, AFNU, низкая стоимость, секретные каникулы',
    ogTitle: 'AFNU - Секретные каникулы',
    ogDescription: 'Теперь вам больше не нужно влезать в долги, чтобы поехать на отдых за границу. Получите свой недорогой отпуск AFNU сегодня',
    metaTitle: 'AFNU - С такими ценами жаль оставаться дома',
    canonical: 'https://ru.afnu.co.il/',
    jsonLd: {
      name: 'AFNU - Секретные каникулы',
      description: 'Бронируйте недорогие туристические пакеты только на AFNU - потому что теперь вам больше не нужно мучиться с бронированием дешевых авиабилетов и отелей за границей',
      slogan: 'Секретные каникулы (авиабилет и отель), которые туристическая индустрия скрывала от вас до сегодняшнего дня'
    }
  }
};

// Function to update meta tags
const updateMetaTags = (lng) => {
  const config = metaConfig[lng] || metaConfig.he;

  // Update document title
  document.title = config.title;

  // Update meta tags
  const updateMetaTag = (name, content, isProperty = false) => {
    const attribute = isProperty ? 'property' : 'name';
    let tag = document.querySelector(`meta[${attribute}="${name}"]`);
    if (tag) {
      tag.setAttribute('content', content);
    }
  };

  updateMetaTag('description', config.description);
  updateMetaTag('keywords', config.keywords);
  updateMetaTag('title', config.metaTitle);
  updateMetaTag('og:title', config.ogTitle, true);
  updateMetaTag('og:description', config.ogDescription, true);
  updateMetaTag('twitter:title', config.ogTitle);
  updateMetaTag('twitter:description', config.ogDescription);

  // Update canonical link
  let canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) {
    canonical.setAttribute('href', config.canonical);
  }

  // Update JSON-LD structured data
  const jsonLdScript = document.querySelector('script[type="application/ld+json"]');
  if (jsonLdScript) {
    const jsonLdData = {
      "@context": "https://schema.org",
      "@type": "Corporation",
      "name": config.jsonLd.name,
      "alternateName": "AFNU",
      "description": config.jsonLd.description,
      "url": config.canonical,
      "logo": "https://www.afnu.co.il/images/logo.png",
      "slogan": config.jsonLd.slogan
    };
    jsonLdScript.textContent = JSON.stringify(jsonLdData, null, 2);
  }
};

// Update document direction, language attributes, and meta tags
const currentDirection = directionMap[currentLanguage] || 'ltr';
document.documentElement.setAttribute('lang', currentLanguage);
document.documentElement.setAttribute('dir', currentDirection);
updateMetaTags(currentLanguage);

// Track initial language load in GTM
window.dataLayer = window.dataLayer || [];
window.dataLayer.push({
  event: 'language_loaded',
  language: currentLanguage,
  direction: currentDirection
});

// Listen for language changes and update document attributes and meta tags
i18n.on('languageChanged', (lng) => {
  const direction = directionMap[lng] || 'ltr';
  document.documentElement.setAttribute('lang', lng);
  document.documentElement.setAttribute('dir', direction);
  updateMetaTags(lng);

  // Track language switch in GTM
  window.dataLayer.push({
    event: 'language_switch',
    language: lng,
    direction: direction
  });
});

export default i18n;
